import type { BprSynthesisRunSummary } from '@protoss/shared';
import type { PillVariant } from '@protoss/ui';
import { Button, Pill } from '@protoss/ui';
import * as React from 'react';
import { bprApi } from '../services/bprApi';

function runStatusVariant(status: BprSynthesisRunSummary['status']): PillVariant {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'accent';
    case 'failed':
      return 'danger';
    case 'partial':
      return 'accent';
    case 'not_applied':
      return 'muted';
    case 'empty':
    default:
      return 'neutral';
  }
}

function formatRunDate(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function BprSynthesisRunsSummary({ engagementUuid }: { engagementUuid: string }) {
  const [runs, setRuns] = React.useState<BprSynthesisRunSummary[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchRuns = React.useCallback(async () => {
    if (!engagementUuid) return;
    setLoading(true);
    setError(null);
    try {
      const response = await bprApi.listSynthesisRuns(engagementUuid);
      setRuns(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch synthesis runs.');
    } finally {
      setLoading(false);
    }
  }, [engagementUuid]);

  React.useEffect(() => {
    void fetchRuns();
  }, [fetchRuns]);

  if (runs.length === 0 && !loading && !error) {
    return null;
  }

  return (
    <div className="bpr-synthesis-runs">
      <div className="bpr-synthesis-runs__header">
        <h4 className="bpr-synthesis-runs__title">Synthesis Runs</h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={loading}
          onClick={() => void fetchRuns()}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {error ? <p className="bpr-dialog__error">{error}</p> : null}

      <div className="bpr-synthesis-runs__cards">
        {runs.map((run) => (
          <div key={`${run.stage}-${run.batchId}`} className="bpr-synthesis-runs__card">
            <div className="bpr-synthesis-runs__card-top">
              <span className="bpr-synthesis-runs__stage">
                {run.stage === 'problems' ? 'Stage A: Problem Detection' : 'Stage B: Solution Options'}
              </span>
              <Pill variant={runStatusVariant(run.status)}>{run.status}</Pill>
            </div>
            <div className="bpr-synthesis-runs__card-meta">
              <span className="bpr-synthesis-runs__batch">Batch {run.batchId.slice(0, 8)}</span>
              <span>·</span>
              <span>{formatRunDate(run.createdAt)}</span>
            </div>
            <div className="bpr-synthesis-runs__counts">
              <span className="bpr-synthesis-runs__stat">
                <strong>{run.appliedCount}</strong> / {run.proposedCount} applied
              </span>
              {run.quarantinedCount > 0 ? (
                <Pill variant="danger">{run.quarantinedCount} quarantined</Pill>
              ) : null}
            </div>
            {run.reviewReasons && run.reviewReasons.length > 0 ? (
              <ul className="bpr-synthesis-runs__reasons">
                {run.reviewReasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
